import { ESG_PLAN } from '@Domains/Esg/EsgPlan';
import { IESGDetails, IProgress } from '@Domains/Progress';

export interface ISubItem {
  name: string;
  path: string;
  thProgress?: boolean;
  enProgress?: boolean;
  isDisabled?: boolean;
  isPass?: boolean;
  hideProgress?: boolean;
}

export interface IMappingSidebarProgress {
  topic: string;
  isPass: boolean;
  score?: number;
  isDisabled?: boolean;
  path?: string;
  subItems?: ISubItem[];
}

const isCompletedSection = (th: boolean, en: boolean): boolean => th && en;

const createSection = (
  name: string,
  path: string,
  thProgress?: boolean,
  enProgress?: boolean
): ISubItem => ({
  name,
  path,
  thProgress,
  enProgress,
});

const getESGSections = (esgPlans: IESGDetails): ISubItem[] => {
  const sections: ISubItem[] = [];
  const characteristicTypes = esgPlans?.characteristicTypes ?? [];

  if (characteristicTypes.includes(ESG_PLAN.DATA_PLATFORM)) {
    sections.push(
      createSection(
        'เปิดเผยข้อมูลจาก ESG Data Platform',
        'esg-plans/platform',
        esgPlans?.esgDataPlatform?.isSectionCompletedTh,
        esgPlans?.esgDataPlatform?.isSectionCompletedEn
      )
    );
  }

  if (characteristicTypes.includes(ESG_PLAN.GREEN_HOUSE)) {
    sections.push(
      createSection(
        'เปิดเผยข้อมูลเกี่ยวข้องกับการปล่อยก๊าซเรือนกระจก',
        'esg-plans/greenhouse',
        esgPlans?.greenhouse?.isSectionCompletedTh || false,
        esgPlans?.greenhouse?.isSectionCompletedEn || false
      )
    );
  }

  return sections;
};

const getBusinessOperationSection = (
  businessOperation: IProgress['businessOperation']
): IMappingSidebarProgress => ({
  topic: 'การประกอบธุรกิจ',
  isPass: isCompletedSection(
    businessOperation.natureOfBusiness.isSectionCompletedTh &&
      businessOperation.impactsOnStakeholdersManagement.isSectionCompletedTh &&
      businessOperation.financialStatements.isSectionCompletedTh,
    businessOperation.natureOfBusiness.isSectionCompletedEn &&
      businessOperation.impactsOnStakeholdersManagement.isSectionCompletedEn &&
      businessOperation.financialStatements.isSectionCompletedEn
  ),
  subItems: [
    createSection(
      'ลักษณะการประกอบธุรกิจ',
      'nature-of-business',
      businessOperation.natureOfBusiness.isSectionCompletedTh,
      businessOperation.natureOfBusiness.isSectionCompletedEn
    ),
    createSection(
      'การจัดการผลกระทบต่อผู้มีส่วนได้เสียในห่วงโซ่คุณค่าของธุรกิจ',
      'impacts-on-stakeholders-management',
      businessOperation.impactsOnStakeholdersManagement.isSectionCompletedTh,
      businessOperation.impactsOnStakeholdersManagement.isSectionCompletedEn
    ),
    createSection(
      'ข้อมูลทางการเงินที่สำคัญ',
      'financial-statements',
      businessOperation.financialStatements.isSectionCompletedTh,
      businessOperation.financialStatements.isSectionCompletedEn
    ),
  ],
});

const checkESGPlanStatus = (esgPlans: IESGDetails): boolean => {
  const hasDataPlatform = esgPlans?.characteristicTypes?.includes(
    ESG_PLAN.DATA_PLATFORM
  );
  const hasGreenHouse = esgPlans?.characteristicTypes?.includes(
    ESG_PLAN.GREEN_HOUSE
  );

  if (hasDataPlatform && !hasGreenHouse) {
    return isCompletedSection(
      Boolean(esgPlans?.esgDataPlatform?.isSectionCompletedTh),
      Boolean(esgPlans?.esgDataPlatform?.isSectionCompletedEn)
    );
  }
  if (!hasDataPlatform && hasGreenHouse) {
    return isCompletedSection(
      Boolean(esgPlans?.greenhouse?.isSectionCompletedTh),
      Boolean(esgPlans?.greenhouse?.isSectionCompletedEn)
    );
  }
  if (hasDataPlatform && hasGreenHouse) {
    return isCompletedSection(
      Boolean(esgPlans?.esgDataPlatform?.isSectionCompletedTh) &&
        Boolean(esgPlans?.esgDataPlatform?.isSectionCompletedEn),
      Boolean(esgPlans?.greenhouse?.isSectionCompletedTh) &&
        Boolean(esgPlans?.greenhouse?.isSectionCompletedEn)
    );
  }
  return false;
};

export function mappingResponseToSidebarProgress(
  reportProgress: IProgress
): IMappingSidebarProgress[] {
  const {
    cgrScore,
    businessOperation,
    goalsAndOperationalPlans,
    esgPlans,
    summarizeReport,
  } = reportProgress;

  const isCGRScorePass = cgrScore?.score ? cgrScore.score >= 90 : false;

  return [
    {
      topic: 'CGR Score',
      isPass: isCGRScorePass,
      score: cgrScore?.score ?? 0,
      path: 'cgr-score',
    },
    getBusinessOperationSection(businessOperation),
    {
      topic: 'แผนการเพิ่มมูลค่าองค์กรและการบริหารความเสี่ยง',
      isPass: isCompletedSection(
        goalsAndOperationalPlans.goalsOperationalPlansAndRiskManagement
          .isSectionCompletedTh,
        goalsAndOperationalPlans.goalsOperationalPlansAndRiskManagement
          .isSectionCompletedEn
      ),
      subItems: [
        createSection(
          'แผนการเพิ่มมูลค่าองค์กรและการบริหารความเสี่ยง',
          'goals-and-operational-plans',
          goalsAndOperationalPlans.goalsOperationalPlansAndRiskManagement
            .isSectionCompletedTh,
          goalsAndOperationalPlans.goalsOperationalPlansAndRiskManagement
            .isSectionCompletedEn
        ),
      ],
    },
    {
      topic: 'แผนการยกระดับ ESG',
      isPass: checkESGPlanStatus(esgPlans),
      subItems: [
        {
          name: 'กรุณาเลือกแผนการยกระดับ ESG ที่ต้องการ',
          path: 'esg-plans',
          hideProgress: true,
        },
        ...getESGSections(esgPlans),
      ],
    },
    {
      topic: 'ขั้นตอนการบันทึกวันที่มีมติจากคณะกรรมการบริษัท',
      isPass: Boolean(
        summarizeReport.committeeApprovalDate && summarizeReport.isDraftReport
      ),
      subItems: [
        {
          name: 'ตรวจสอบข้อมูล',
          path: 'review-summary-report',
          isPass: Boolean(summarizeReport.isDraftReport),
        },
        {
          name: 'บันทึกวันที่มีมติจากคณะกรรมการบริษัท',
          path: 'review-summary-report',
          isDisabled: !isCGRScorePass,
          isPass: Boolean(summarizeReport.committeeApprovalDate),
        },
      ],
    },
    {
      topic: 'ขั้นตอนการเผยแพร่รายงาน',
      isPass: Boolean(summarizeReport.publishedDate),
      isDisabled: !isCGRScorePass,
      subItems: [
        {
          name: 'เผยแพร่รายงาน',
          path: 'review-summary-report',
          isDisabled: !isCGRScorePass,
          isPass: Boolean(summarizeReport.publishedDate),
        },
      ],
    },
  ];
}
